import React from 'react';

/**
 * Typographic arrow (U+2192); color is set in BattleActionLog.css on the
 * `.battle-action-log-drawer-resource-delta-line--intel` / `--general` rules that also target `.battle-action-log-drawer-resource-delta-arrow` (same as numbers).
 */
export function BattleActionLogResourceDeltaArrow() {
  return (
    <span className="battle-action-log-drawer-resource-delta-arrow" aria-hidden="true">
      →
    </span>
  );
}

/**
 * Resource deltas for battle action log rows (intel, general health).
 * Server may set affectedIntel / affectedGeneralHealth, or unit-attacked-general outcome fields.
 * @param {object} entry - actionLog entry
 * @returns {{ kind: 'intel' | 'general', before: number, after: number }[]}
 */
export function getBattleActionLogResourceDeltaLines(entry) {
  if (!entry || typeof entry !== 'object') return [];

  const items = [];

  if (
    entry.affectedIntel &&
    typeof entry.affectedIntel.before === 'number' &&
    typeof entry.affectedIntel.after === 'number' &&
    entry.affectedIntel.before !== entry.affectedIntel.after
  ) {
    items.push({
      kind: 'intel',
      before: entry.affectedIntel.before,
      after: entry.affectedIntel.after
    });
  }

  let general = entry.affectedGeneralHealth;
  if (
    !general &&
    entry.outcome &&
    entry.outcome.isGeneralAttack === true &&
    typeof entry.outcome.targetHealthBefore === 'number' &&
    typeof entry.outcome.targetHealthAfter === 'number' &&
    entry.outcome.targetHealthBefore !== entry.outcome.targetHealthAfter
  ) {
    general = {
      before: entry.outcome.targetHealthBefore,
      after: entry.outcome.targetHealthAfter
    };
  }

  if (
    general &&
    typeof general.before === 'number' &&
    typeof general.after === 'number' &&
    general.before !== general.after
  ) {
    items.push({
      kind: 'general',
      before: general.before,
      after: general.after
    });
  }

  return items;
}

/**
 * Per-unit HP before → after for `unit-attacked` drawer thumbnails (attacker thumb, then target thumb).
 * Uses server `entry.outcome` from combat (attackerHealthBefore/After, targetHealthBefore/After).
 * @param {object} entry
 * @returns {[null | { before: number, after: number }, null | { before: number, after: number }]}
 */
export function getBattleActionLogAttackThumbHealthLines(entry) {
  const empty = [null, null];
  if (!entry || entry.type !== 'unit-attacked' || !entry.outcome) return empty;
  const oc = entry.outcome;
  let attackerLine = null;
  if (
    typeof oc.attackerHealthBefore === 'number' &&
    typeof oc.attackerHealthAfter === 'number' &&
    oc.attackerHealthBefore !== oc.attackerHealthAfter
  ) {
    attackerLine = { before: oc.attackerHealthBefore, after: oc.attackerHealthAfter };
  }
  let targetLine = null;
  if (
    typeof oc.targetHealthBefore === 'number' &&
    typeof oc.targetHealthAfter === 'number' &&
    oc.targetHealthBefore !== oc.targetHealthAfter
  ) {
    targetLine = { before: oc.targetHealthBefore, after: oc.targetHealthAfter };
  }
  return [attackerLine, targetLine];
}

/**
 * For `unit-attacked`, intel / general deltas belong under the thumbnail whose owner matches
 * `affectedIntel.playerId` / `affectedGeneralHealth.playerId` (not in a single right-aligned block).
 */
export function partitionUnitAttackColumnIntelGeneral(entry) {
  const empty = {
    attackerIntel: null,
    targetIntel: null,
    attackerGeneral: null,
    targetGeneral: null
  };
  if (!entry || entry.type !== 'unit-attacked') return empty;

  const attackerOwner =
    entry.attacker?.playerId != null
      ? String(entry.attacker.playerId)
      : entry.playerId != null
        ? String(entry.playerId)
        : null;
  const targetOwner = entry.target?.playerId != null ? String(entry.target.playerId) : null;

  let attackerIntel = null;
  let targetIntel = null;
  const ai = entry.affectedIntel;
  if (
    ai &&
    typeof ai.before === 'number' &&
    typeof ai.after === 'number' &&
    ai.before !== ai.after
  ) {
    const intelPid = ai.playerId != null ? String(ai.playerId) : attackerOwner;
    const delta = { before: ai.before, after: ai.after };
    if (attackerOwner && intelPid === attackerOwner) attackerIntel = delta;
    else if (targetOwner && intelPid === targetOwner) targetIntel = delta;
    else if (attackerOwner) attackerIntel = delta;
    else if (targetOwner) targetIntel = delta;
  }

  let attackerGeneral = null;
  let targetGeneral = null;
  const gh = entry.affectedGeneralHealth;
  if (
    gh &&
    gh.playerId != null &&
    typeof gh.before === 'number' &&
    typeof gh.after === 'number' &&
    gh.before !== gh.after
  ) {
    const gp = String(gh.playerId);
    const delta = { before: gh.before, after: gh.after };
    if (attackerOwner && gp === attackerOwner) attackerGeneral = delta;
    else if (targetOwner && gp === targetOwner) targetGeneral = delta;
  }

  return { attackerIntel, targetIntel, attackerGeneral, targetGeneral };
}

/**
 * Resource lines for the drawer: global block (below thumbs) plus per-column intel/general for unit attacks.
 */
export function getBattleActionLogResourceDeltaLinesForDrawer(entry) {
  const lines = getBattleActionLogResourceDeltaLines(entry);
  if (entry?.type !== 'unit-attacked') {
    return { globalLines: lines, attackColumns: null };
  }
  const attackColumns = partitionUnitAttackColumnIntelGeneral(entry);
  const placedIntel = !!(attackColumns.attackerIntel || attackColumns.targetIntel);
  const placedGeneral = !!(attackColumns.attackerGeneral || attackColumns.targetGeneral);
  const globalLines = lines.filter((line) => {
    if (line.kind === 'intel' && placedIntel) return false;
    if (line.kind === 'general' && placedGeneral) return false;
    return true;
  });
  return { globalLines, attackColumns };
}
